import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchInput from "../../common/search-input/SearchInput";
import "./TeamSection.css";

const TeamMemberCard = React.memo(({ member }) => {
  const navigate = useNavigate();

  const getImageUrl = useCallback(() => {
    const getDefaultAvatar = (name) => {
      const parts = name.trim().split(" ");
      let initials =
        parts.length >= 2
          ? parts[0][0] + parts[parts.length - 1][0]
          : name.substring(0, 2);

      const colors = [
        "FFEAA7",
        "FF6B6B",
        "4ECDC4",
        "95E1D3",
        "F38181",
        "AA96DA",
        "FCBAD3",
        "A8E6CF",
        "FFD3B6",
        "FFAAA5",
      ];
      const hash = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
      return `https://ui-avatars.com/api/?name=${initials.toUpperCase()}&background=${colors[hash % colors.length]}&color=fff&size=300`;
    };

    if (
      !member.photo_url ||
      member.is_display_img === 0 ||
      member.is_display_img === "0"
    ) {
      return getDefaultAvatar(member.name);
    }
    if (member.photo_url.startsWith("http")) return member.photo_url;
    return `${process.env.REACT_APP_URL}/storage/${member.photo_url.replace(/^\//, "")}`;
  }, [member]);

  return (
    <div
      className="tm-card"
      onClick={() => member.id && navigate(`/team-members/view/${member.id}`)}
    >
      <div className="tm-card-inner">
        <img
          src={getImageUrl()}
          alt={member.name}
          className="tm-img"
          loading="lazy"
        />
        <div className="tm-overlay">
          <h4 className="tm-name">{member.name}</h4>
          <p className="tm-role">
            {member.designation || member.position || "Professional"}
          </p>
        </div>
      </div>
    </div>
  );
});

const TeamSection = () => {
  const { data: teamData, loading } = useSelector((state) => state.team);
  const { data: servicesData } = useSelector((state) => state.services);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeService, setActiveService] = useState("all");

  // Updated to 6 items initially
  const INITIAL_ITEMS = 4;
  const [itemsToShow, setItemsToShow] = useState(INITIAL_ITEMS);

  // Reset items count when filters change
  useEffect(() => {
    setItemsToShow(INITIAL_ITEMS);
  }, [searchTerm, activeService]);

  const allMembers = teamData || [];

  const departments = useMemo(() => {
    if (servicesData?.length) {
      return servicesData
        .filter((s) => s.status === "active")
        .sort((a, b) => a.order - b.order)
        .map((s) => s.slug);
    }
    return [];
  }, [servicesData]);

  const getDepartmentLabel = useCallback(
    (slug) => {
      const svc = servicesData?.find((s) => s.slug === slug);
      return svc ? svc.name : slug;
    },
    [servicesData],
  );

  const visibleMembers = useMemo(() => {
    const founderName = "rakesh panchal";
    const filtered = allMembers.filter((m) => {
      if (m.name.toLowerCase() === founderName) return false;
      const matchDept =
        activeService === "all" || m?.service?.slug === activeService;
      const matchSearch =
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.designation || "").toLowerCase().includes(searchTerm.toLowerCase());
      return matchDept && matchSearch;
    });

    const founder = allMembers.find(
      (m) => m.name.toLowerCase() === founderName,
    );
    return founder ? [founder, ...filtered] : filtered;
  }, [allMembers, activeService, searchTerm]);

  if (loading) return <div className="tm-status">Loading our team...</div>;

  return (
    <section className="tm-wrapper">
      <div className="tm-header">
        <h2>Our Team</h2>
        <p>Meet the experts driving our financial excellence</p>
      </div>

      <div className="tm-filters">
        <SearchInput
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          placeholder="Search member name..."
        />

        <div className="tm-tabs-container">
          <button
            className={`tm-tab-btn ${activeService === "all" ? "active" : ""}`}
            onClick={() => setActiveService("all")}
          >
            All Members
          </button>
          {departments.map((dept) => (
            <button
              key={dept}
              className={`tm-tab-btn ${activeService === dept ? "active" : ""}`}
              onClick={() => setActiveService(dept)}
            >
              {getDepartmentLabel(dept)}
            </button>
          ))}
        </div>
      </div>

      <div className="tm-grid-container">
        {visibleMembers.length === 0 ? (
          <div className="tm-no-results">
            <p>No team members found matching your criteria.</p>
            <button
              className="tm-clear-btn"
              onClick={() => {
                setSearchTerm("");
                setActiveService("all");
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="tm-grid">
              {visibleMembers.slice(0, itemsToShow).map((m) => (
                <TeamMemberCard key={m.id} member={m} />
              ))}
            </div>

            {/* View More Logic */}
            {visibleMembers.length > itemsToShow && (
              <div className="tm-load-more">
                <button onClick={() => setItemsToShow((prev) => prev + 6)}>
                  View More Members
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default TeamSection;
